/* eslint-disable max-lines */
import React, {useMemo} from "react";
import useVisibility from "../../hooks/useVisibility";
import {useTranslation} from "react-i18next";

import {
  DisplayParametricResultMode,
  EducationSessionRecordState,
  EducationSessionResult,
  Challenge,
  CompetenceAnalysisState,
  LanguageCode,
  ChallengeMode,
  Maybe,
  FeedbackStatusEnumType,
  SessionResultFeedback,
} from "../../schema";
import ReactLink from "../ReactLink";
import FullscreenVideo from "../../ui/fullscreenvideo";
import RecordLink from "../RecordLink";
import {ParametricResultHelp} from "../ParametricResultHelp";
import Link from "../../ui/link";

import {getParametricScore} from "../../utils";
import {reverse} from "../../routing";

import cn from "classnames";
import useAdaptiveClasses from "../../hooks/useAdaptiveClasses";
import classes from "./ResultsDisplay.module.css";
import Spinner from "../../ui/spinner";
import ReactMarkdown from "react-markdown";
import {
  getGeneralEmotionsData,
  getPassedOrFailedHelpText,
  getResultDisplayFields,
  getScoresData,
} from "../../utils/player/utils";

type props = Omit<React.ComponentProps<"div">, "results"> & {
  children?: React.ReactNode;
  challenge: Pick<
    Challenge,
    | "id"
    | "mode"
    | "languageCode"
    | "showDetailedResults"
    | "displayParametricResultMode"
    | "isSessionResultFeedbackEnabled"
  >;
  results: Pick<
    EducationSessionResult,
    | "passed"
    | "score"
    | "time"
    | "usedHints"
    | "record"
    | "recordState"
    | "generalSentimentNeutral"
    | "generalSentimentPositive"
    | "generalSentimentNegative"
    | "generalEmotionAnger"
    | "generalEmotionDisgust"
    | "generalEmotionEnthusiasm"
    | "generalEmotionFear"
    | "generalEmotionGuilt"
    | "generalEmotionJoy"
    | "generalEmotionNeutral"
    | "generalEmotionSadness"
    | "generalEmotionShame"
    | "generalEmotionSurprise"
    | "competenceResults"
    | "competenceAnalysisState"
    | "parametricResults"
    | "isUserInputAnalysisReady"
  >;
  canDisplayResults?: boolean;
  adaptive?: boolean;
  hideTitle?: boolean;
  hideHelpText?: boolean;
  resultMessage?: string;
  customScores: boolean;
  matchedDescription?: string;
  multiDescriptions?: {parameter: string | undefined; message: Maybe<string> | undefined}[];
  showDetailed: boolean;
  sessionResultFeedback?: SessionResultFeedback;
  isAnalysisTakingTooLong?: boolean;
};

const ResultsDisplay = function ({
  className: classNameEx,
  children,
  challenge,
  results,
  canDisplayResults,
  adaptive,
  hideTitle,
  hideHelpText,
  showDetailed,
  resultMessage,
  customScores,
  matchedDescription,
  multiDescriptions,
  sessionResultFeedback,
  isAnalysisTakingTooLong,
  ...props
}: props) {
  const {t} = useTranslation();
  const challengeId = challenge.id;
  const challengeMode = challenge.mode;

  const [videoVisible, showVideo, hideVideo] = useVisibility();

  const ac = useAdaptiveClasses(classes, adaptive);

  let displayFields: string[] = useMemo(() => getResultDisplayFields(challengeMode), [challengeMode]);

  const helpText = getPassedOrFailedHelpText(results.passed, challengeMode, t);

  const scores = getScoresData(results, challengeMode, t);

  const emotions = useMemo(() => {
    if (results.isUserInputAnalysisReady === false) return [];
    return getGeneralEmotionsData(results, t);
  }, [results, t]);

  return (
    <div className={cn(classes.root, classNameEx)} {...props}>
      {!hideTitle && (
        <h2 className={ac.title}>
          {results.passed ? t("components.ResultsDisplay.passed.title") : t("components.ResultsDisplay.failed.title")}
        </h2>
      )}
      {!hideHelpText && <span className={ac.helpText}>{helpText}</span>}
      <div className={ac.scoresRow}>
        {scores.map(({field, title, getValue}) => {
          if (!displayFields.includes(field)) return <></>;
          return (
            <div className={ac.score}>
              <span className={ac.scoreName}>{title}</span>
              <span className={ac.scoreValue}>{getValue()}</span>
            </div>
          );
        })}
      </div>

      {challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && (
        <>
          <div className={ac.helpText}>
            {t("components.ResultsDisplay.parametricResults.title")}
            <ParametricResultHelp displayParametricResultMode={challenge.displayParametricResultMode} />
          </div>
          <div className={ac.scoresRow}>
            {results.parametricResults.map((result, i) => (
              <div className={ac.score} key={i}>
                <span className={ac.scoreName}>{result.parameterName}</span>
                <span className={ac.scoreValue}>
                  {getParametricScore(result, challenge.displayParametricResultMode)}
                  {challenge.displayParametricResultMode === DisplayParametricResultMode.PERCENTAGE && "%"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {resultMessage && (
        <div className={showDetailed ? classes.resultMessageContainer : classes.resultMessageContainerCenter}>
          {resultMessage}
        </div>
      )}

      {!multiDescriptions &&
        (customScores ||
          challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST ||
          challenge.mode === ChallengeMode.COMPETENCE_TEST) && (
          <div className={showDetailed ? classes.resultMessageContainer : classes.resultMessageContainerCenter}>
            {matchedDescription}
          </div>
        )}

      {multiDescriptions &&
        multiDescriptions.map((desc) =>
          desc.message ? (
            <div className={classes.resultMessageContainer}>{desc.parameter + ": " + desc.message}</div>
          ) : (
            <></>
          ),
        )}

      {challenge.languageCode === LanguageCode.RU_RU && challenge.showDetailedResults && (
        <div>
          {!(challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) && (
            <>
              <span className={ac.helpText}>{t("components.ResultsDisplay.generalSentiment")}</span>

              <div className={ac.subHelpText}>{t("components.ResultsDisplay.mood")}</div>

              <div className={ac.scoresRow}>
                <div className={ac.score}>
                  <span className={ac.scoreName}>{t("components.ResultsDisplay.sentiment.positive")}</span>
                  <span className={ac.scoreValue}>
                    {results?.generalSentimentPositive ?? "0"}
                    {"%"}
                  </span>
                </div>
                <div className={ac.score}>
                  <span className={ac.scoreName}>{t("components.ResultsDisplay.sentiment.neutral")}</span>
                  <span className={ac.scoreValue}>
                    {results?.generalSentimentNeutral ?? "0"}
                    {"%"}
                  </span>
                </div>
                <div className={ac.score}>
                  <span className={ac.scoreName}>{t("components.ResultsDisplay.sentiment.negative")}</span>
                  <span className={ac.scoreValue}>
                    {results?.generalSentimentNegative ?? "0"}
                    {"%"}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Emotion Results */}
          {/* Emotion Analysis Loading */}
          {results.isUserInputAnalysisReady !== true && (
            <>
              <div className={ac.subHelpText}>{t("components.ResultsDisplay.generalEmotion")}</div>
              {isAnalysisTakingTooLong ? (
                <div className={ac.feedbackText}>{t("components.ResultsDisplay.analysisTakingTooLongAlt")}</div>
              ) : (
                <div className={ac.scoresRow}>
                  <div className={cn(ac.score, ac.analysisRow)}>
                    <span className={ac.scoreName}>{t("components.ResultsDisplay.analyzing")}</span>
                    <Spinner className={classes.spinner} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Emotion Analysis Ready */}
          {results.isUserInputAnalysisReady === true && emotions.length > 0 && (
            <>
              <div className={ac.subHelpText}>{t("components.ResultsDisplay.generalEmotion")}</div>

              <div className={ac.scoresRow}>
                {emotions.map((emotion) => (
                  <div className={ac.score} key={emotion.label}>
                    <span className={ac.scoreName}>{emotion.label}</span>
                    <span className={ac.scoreValue}>
                      {emotion.score ?? "0"}
                      {"%"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {(challengeMode === ChallengeMode.COMPETENCE_TEST || challengeMode === ChallengeMode.RPG_MODE) && (
        <>
          {challenge.isSessionResultFeedbackEnabled &&
            sessionResultFeedback &&
            sessionResultFeedback.feedbackStatus !== FeedbackStatusEnumType.DISABLED && (
              <>
                <div className={ac.helpText}>{t("components.ResultsDisplay.generativeFeedback.title")}:</div>
                {sessionResultFeedback.feedbackStatus === FeedbackStatusEnumType.IN_PROGRESS &&
                  (isAnalysisTakingTooLong ? (
                    <div className={ac.feedbackText}>{t("components.ResultsDisplay.analysisTakingTooLong")}</div>
                  ) : (
                    <div className={ac.scoresRow}>
                      <div className={cn(ac.score, ac.analysisRow)}>
                        <span className={ac.scoreName}>{t("components.ResultsDisplay.analyzing")}</span>
                        <Spinner className={classes.spinner} />
                      </div>
                    </div>
                  ))}

                {sessionResultFeedback.feedbackStatus === FeedbackStatusEnumType.READY &&
                  sessionResultFeedback.text && (
                    <div className={ac.feedbackText}>
                      <ReactMarkdown>{sessionResultFeedback.text}</ReactMarkdown>
                    </div>
                  )}

                {sessionResultFeedback.feedbackStatus === FeedbackStatusEnumType.ERROR && (
                  <span className={ac.scoreError}>{t("components.ResultsDisplay.competenceError")}</span>
                )}
              </>
            )}

          {/* Competence Results */}
          {results.competenceAnalysisState === CompetenceAnalysisState.READY &&
            results.competenceResults &&
            results.competenceResults.length > 0 && (
              <>
                <div className={ac.helpText}>{t("components.ResultsDisplay.competenceResults")}</div>
                <div className={classes.competenceContainer}>
                  {results.competenceResults.map((competenceResult, index) => (
                    <div key={index} className={classes.competenceBlock}>
                      <div className={ac.score}>
                        <span className={ac.scoreName}>{competenceResult.competence}</span>
                        <span className={ac.scoreValue}>
                          {(competenceResult.score * 100).toFixed(0)}
                          {"%"}
                        </span>
                      </div>
                      {competenceResult.indicators && competenceResult.indicators.length > 0 && (
                        <div className={classes.indicatorsList}>
                          {competenceResult.indicators.map((indicator, idx) => (
                            <div key={idx} className={classes.indicatorItem}>
                              <span className={classes.indicatorName}>
                                {indicator.indicator}:{" "}
                                {indicator.isPresent
                                  ? t("components.ResultsDisplay.indicator.present")
                                  : t("components.ResultsDisplay.indicator.notPresent")}
                              </span>
                              {indicator.example && (
                                <>
                                  {" "}
                                  <span className={classes.indicatorExample}>
                                    ({t("components.ResultsDisplay.indicator.example")}: "{indicator.example}")
                                  </span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

          {/* Competence Analysis Loading */}
          {results.competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS && (
            <>
              <div className={ac.subHelpText}>{t("components.ResultsDisplay.competenceResults")}</div>
              {isAnalysisTakingTooLong ? (
                <div className={ac.feedbackText}>{t("components.ResultsDisplay.analysisTakingTooLong")}</div>
              ) : (
                <div className={ac.scoresRow}>
                  <div className={cn(ac.score, ac.analysisRow)}>
                    <span className={ac.scoreName}>{t("components.ResultsDisplay.competenceAnalyzing")}</span>
                    <Spinner className={classes.spinner} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Competence Analysis Error */}
          {(results.competenceAnalysisState === CompetenceAnalysisState.ERROR ||
            results.competenceAnalysisState === CompetenceAnalysisState.UNKNOWN) && (
            <>
              <div className={ac.subHelpText}>{t("components.ResultsDisplay.competenceResults")}</div>
              <span className={ac.scoreError}>{t("components.ResultsDisplay.competenceError")}</span>
            </>
          )}
        </>
      )}

      {canDisplayResults ? (
        <ReactLink href={reverse("playerResults", {challengeId: challengeId})} className={ac.resultsLink}>
          {t("components.ResultsDisplay.results")}
        </ReactLink>
      ) : (
        results.recordState !== EducationSessionRecordState.SKIPPED && (
          <>
            <RecordLink as={Link} className={ac.recordLink} state={results.recordState} onClick={showVideo} />
            {videoVisible && <FullscreenVideo src={results.record ?? undefined} onClose={hideVideo} />}
          </>
        )
      )}

      {children}
    </div>
  );
};

export default ResultsDisplay;
